import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  LuCheck,
  LuExternalLink,
  LuFileText,
  LuLogOut,
  LuMail,
  LuPhone,
  LuSave,
  LuSparkles,
  LuUpload,
  LuUser,
} from "react-icons/lu";
import { Btn, Badge, Field, Spinner, Skel, Note, Dialog, initialsOf } from "../components/Ui";
import { Logout } from "../../store/actions/AuthActions";
import { setProfileData, setJobsList, setJobsApplied } from "../../store/actions/actions";
import { formatPhoneByCountry, getCallingCode } from "../phoneFormat";
import "./ResumeUpload.css";

/* Endpoints — identical to legacy Resume.js */
const DETAILS_API = "https://us-east1-foursssolutions.cloudfunctions.net/send_resume_details_front_end_v2";
const UPLOAD_API = "https://us-east1-foursssolutions.cloudfunctions.net/Resume_upload_bucket_store_in_table_v2";

const STAGES = {
  sent: { pct: 25, text: "Your resume has been uploaded to the server." },
  screening: { pct: 55, text: "Your resume is being screened." },
  preparing: { pct: 85, text: "Our CareerSavvy AI is preparing your profile." },
  ready: { pct: 100, text: "" },
};
const STAGE_ORDER = ["sent", "screening", "preparing"];

export default function ResumeUpload() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userEmail = useSelector((state) => state.auth.auth.email);
  const { profileData, fileResume, detailedJob, resumeJson } = useSelector((state) => state.profile);

  const [file, setFile] = useState(null);
  const [chosenFiles, setChosenFiles] = useState([]);
  const [uploadedNames, setUploadedNames] = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [stageOpen, setStageOpen] = useState(false);
  const [stage, setStage] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [isCounting, setIsCounting] = useState(false);

  const inputRef = useRef(null);
  const stageTimersRef = useRef([]);

  /* clear the staged post-upload timers if the page unmounts mid-flow */
  useEffect(
    () => () => {
      stageTimersRef.current.forEach((id) => clearTimeout(id));
      stageTimersRef.current = [];
    },
    []
  );

  /* ---------- fetch profile + uploaded file names (payload identical to legacy) ---------- */
  const fetchFilenames = async () => {
    try {
      const queryObj = { emailid: `${userEmail}` };
      const response = await fetch(DETAILS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(queryObj),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const data = await response.json();
      /* parsing kept byte-identical to legacy Resume.js — a malformed
         response throws into the catch and nothing gets dispatched */
      const extractedFilenames = data.file_details.map((f) => f.file_name);
      setUploadedNames(extractedFilenames);
      const extractedProfileData = data.user_details[0];
      dispatch(setProfileData(extractedProfileData));
      setLoadError("");
    } catch (error) {
      setLoadError(error.message || "Failed to fetch data");
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    if (userEmail !== "") {
      fetchFilenames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  /* ---------- file selection / validation (rules + copy identical to legacy) ---------- */
  const acceptFile = (candidate, resetInput) => {
    if (!candidate) return;
    const allowedExtensionsRegex = /\.(doc|docx|pdf)$/i;
    const allowedMimes = new Set([
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ]);
    /* Browsers report no MIME type for .doc from some sources and for most
       drag-and-drop files, so requiring a MIME match rejected valid resumes.
       The extension is the reliable signal; the MIME only has to not conflict. */
    const extOk = allowedExtensionsRegex.test(candidate.name);
    const mimeOk = !candidate.type || allowedMimes.has(candidate.type);
    if (!extOk || !mimeOk) {
      toast.error("Only PDF, DOC, or DOCX files are allowed.");
      if (resetInput && inputRef.current) inputRef.current.value = "";
      return;
    }
    if (candidate.size === 0) {
      toast.error("That file is empty. Please choose your resume file.");
      if (resetInput && inputRef.current) inputRef.current.value = "";
      return;
    }
    if (candidate.size > 10 * 1024 * 1024) {
      toast.error("That file is over 10 MB. Please upload a smaller resume.");
      if (resetInput && inputRef.current) inputRef.current.value = "";
      return;
    }
    setChosenFiles([candidate]);
    setFile(candidate);
  };

  const handleFileChange = (e) => {
    acceptFile(e.target.files && e.target.files[0], true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files && e.dataTransfer.files[0], false);
  };

  /* ---------- post-upload flow ----------
     This used to be pure theatre: fixed 15s / 35s / 65s timers that advanced
     the stage regardless of whether the resume had actually been parsed, so
     everyone waited the full 65 seconds even when it was ready in ten.
     Now it polls for the real signal — the parsed resume_json coming back
     from the details endpoint — and moves on the moment it lands. The old
     timings stay as the upper bound, so this is never slower than before. */
  const READY_TIMEOUT_MS = 65000;
  const POLL_EVERY_MS = 2500;

  function showReminderClicked() {
    setStageOpen(true);
    const t = stageTimersRef.current;
    const startedAt = Date.now();
    let settled = false;
    /* the PREVIOUS resume_json is still on the record until the new upload is
       parsed, so presence alone would settle on the first poll. Snapshot it
       and wait for it to actually change. The store holds it parsed while the
       endpoint returns it as a string, so normalise both sides. */
    const norm = (v) => {
      try {
        return JSON.stringify(typeof v === "string" ? JSON.parse(v) : v);
      } catch (e) {
        return String(v);
      }
    };
    const before = norm(resumeJson);

    const finish = () => {
      if (settled) return;
      settled = true;
      if (detailedJob !== "") {
        navigate(`/job/detailed?id=${detailedJob}`);
        /* legacy called setControlFeatures(true) without dispatch — intentional no-op preserved */
      }
      setStage("ready");
      setIsCounting(true);
      setCountdown(5);
      t.push(setTimeout(() => dispatch(Logout(navigate)), 6000));
    };

    t.push(setTimeout(() => setStage("sent"), 300));

    /* stage labels still advance on their own so the panel keeps moving while
       we wait; they are cosmetic, `finish` is what actually ends the flow */
    t.push(setTimeout(() => !settled && setStage("screening"), 6000));
    t.push(setTimeout(() => !settled && setStage("preparing"), 18000));

    const poll = async () => {
      if (settled) return;
      try {
        const res = await fetch(DETAILS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ emailid: `${userEmail}` }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data && data.resume_json && norm(data.resume_json) !== before) {
            finish();
            return;
          }
        }
      } catch (err) {
        /* a failed poll just means we try again until the timeout */
      }
      if (Date.now() - startedAt < READY_TIMEOUT_MS) {
        t.push(setTimeout(poll, POLL_EVERY_MS));
      } else {
        /* the parse never produced anything readable. Never sign the user out
           on this path — the previous resume is still the one on file. */
        settled = true;
        setStageOpen(false);
        setLoadError(
          "We could not read that file, so your previous resume is still in place. " +
            "Try a text-based PDF or DOCX — scanned images and password-protected files cannot be read."
        );
        toast.error("We couldn't read that resume. Your previous one is unchanged.");
      }
    };
    t.push(setTimeout(poll, POLL_EVERY_MS));
  }

  useEffect(() => {
    let timer;
    if (isCounting && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    if (countdown === 0) {
      clearInterval(timer);
      setIsCounting(false);
    }
    return () => clearInterval(timer);
  }, [isCounting, countdown]);

  function remConfirm() {
    dispatch(Logout(navigate));
  }

  function watchVideo() {
    window.open("https://www.youtube.com/watch?v=Nkl5TiWo6Bs", "_blank");
  }

  /* ---------- upload (multipart payload identical to legacy) ---------- */
  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload.");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("email_id", userEmail);
    try {
      const response = await fetch(UPLOAD_API, {
        method: "POST",
        body: formData,
      });
      /* response.ok was never checked, so a rejected upload still ran the
         whole success flow and the user was told it had worked */
      const uploadBody = await response.json().catch(() => null);
      if (!response.ok || (uploadBody && (uploadBody.error || uploadBody.status === "error"))) {
        throw new Error((uploadBody && (uploadBody.error || uploadBody.message)) || "Upload rejected");
      }
      setFile(null);
      setChosenFiles([]);
      fetchFilenames();
      dispatch(setJobsList([]));
      dispatch(setJobsApplied([]));
      setUploading(false);
      showReminderClicked();
    } catch (error) {
      console.error("Error:", error);
      setLoadError(
        "We couldn't upload that file, so your previous resume is still in place. Please try again."
      );
      toast.error("Upload failed. Your previous resume is unchanged.");
      setUploading(false);
    }
  };

  /* ---------- save profile then upload (payload + order identical to legacy) ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    /* Don't require a phone number to save details — a fresh/India profile with no
       phone was blocked from saving its name/about at all (Pavithra Resume #1 /
       India #2). Only a name is essential. */
    if (!profileData?.first_name || !profileData?.last_name) {
      toast.error("Please add your first and last name before saving.");
      return;
    }
    setUploading(true);
    try {
      /* mobile_no is a numeric column — sending "" makes the backend 400
         ("Failed to update user details"), which is why a no-phone profile
         could never save (Pavithra Resume #1 / India #2). Only send the phone
         when it actually has digits. */
      const columns = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        about_me: profileData.about_me || "",
      };
      const phoneDigits = String(profileData.mobile_no || "").replace(/\D/g, "");
      if (phoneDigits) columns.mobile_no = phoneDigits;
      const updateObj = {
        emailid: userEmail,
        modify: { columns },
      };
      const response = await fetch(DETAILS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateObj),
      });
      if (!response.ok) {
        setUploading(false);
        throw new Error("Failed to update data");
      }
      /* Details saved. Only upload a resume if the user actually chose a new
         file — you can now update just your details (Pavithra Resume #1). */
      if (chosenFiles.length >= 1) {
        handleUpload();
      } else {
        setUploading(false);
        toast.success("Your details have been saved.");
      }
    } catch (error) {
      setUploading(false);
      toast.error(error.message || "Failed to update data");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile_no") {
      // Country-aware phone formatting (Muni 2026-08-22) — same behaviour as the
      // Profile page; international numbers are grouped, not forced to a US shape.
      const cc = profileData?.country_code || getCallingCode(profileData?.country) || "";
      dispatch(
        setProfileData({ ...profileData, country_code: cc, mobile_no: formatPhoneByCountry(value, profileData?.country, cc) })
      );
      return;
    }
    dispatch(setProfileData({ ...profileData, [name]: value }));
  };

  const currentResume = fileResume || uploadedNames[0] || "";
  const stageInfo = STAGES[stage];
  const stageIdx = STAGE_ORDER.indexOf(stage);

  /* ============================ render ============================ */
  return (
    <div className="stack-4 ru-wrap">
      <div>
        {/* the nav calls this Resume; the heading calling it "Update your
            profile" made it read as a second, competing Profile page */}
        <h1 className="t-h1">Your resume</h1>
        <p className="t-sm mt-1">
          Upload the resume our AI reads to personalise your jobs, interviews and coaching.
        </p>
      </div>

      {loadError ? (
        <Note tone="danger">
          <span className="grow">We could not load your profile details ({loadError}).</span>
          <button
            type="button"
            onClick={() => {
              setProfileLoading(true);
              setLoadError("");
              fetchFilenames();
            }}
            style={{ fontWeight: 600, textDecoration: "underline" }}
          >
            Retry
          </button>
        </Note>
      ) : null}

      <form onSubmit={handleSubmit}>
        <div className="ru-grid">
          {/* ---------- profile details (glass tile) ---------- */}
          <section className="ld-glass ru-tile">
            <h2 className="t-h3 row mb-3" style={{ gap: 8 }}>
              <LuUser size={16} className="t-accent" /> Your details
            </h2>
            {profileLoading ? (
              <div className="stack-3">
                <Skel h={42} />
                <Skel h={42} />
                <Skel h={42} />
                <Skel h={80} />
              </div>
            ) : (
              <div className="stack-3">
                <div className="grid-2">
                  <Field label="First name">
                    <div className="input-wrap has-icon">
                      <span className="input-icon">
                        <LuUser size={15} />
                      </span>
                      <input
                        type="text"
                        className="input"
                        name="first_name"
                        placeholder="Enter name"
                        value={profileData?.first_name || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </Field>
                  <Field label="Last name">
                    <div className="input-wrap has-icon">
                      <span className="input-icon">
                        <LuUser size={15} />
                      </span>
                      <input
                        type="text"
                        className="input"
                        name="last_name"
                        placeholder="Last name"
                        value={profileData?.last_name || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </Field>
                </div>
                <Field label="Phone number">
                  <div className="input-wrap has-icon">
                    <span className="input-icon">
                      <LuPhone size={15} />
                    </span>
                    <input
                      type="text"
                      className="input"
                      name="mobile_no"
                      placeholder="Phone no."
                      value={profileData?.mobile_no || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </Field>
                <Field label="Let us know about yourself">
                  <textarea
                    className="input"
                    rows={5}
                    name="about_me"
                    placeholder="A short introduction about you"
                    value={profileData?.about_me || ""}
                    onChange={handleInputChange}
                  />
                </Field>
                {/* Save the details on their own — you don't need a new file to
                    save name/phone/about (the file panel's button also saves). */}
                <div>
                  <button type="submit" className="btn btn-mint btn-pill" disabled={uploading}>
                    <LuSave size={15} /> Save details
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* ---------- base resume (glass tile: file row / empty, dropzone, submit) ---------- */}
          <section className="ld-glass ru-tile">
            <h2 className="t-h3 row mb-3" style={{ gap: 8 }}>
              <LuFileText size={16} className="t-accent" /> Base resume
            </h2>
            <div className="stack-3">
              {currentResume ? (
                <div>
                  <div className="ld-eyebrow mb-1">Your uploaded resume</div>
                  <div className="ru-file">
                    <span className="ld-stat-icon">
                      <LuFileText size={18} />
                    </span>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div
                        className="t-sm t-ink"
                        style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={currentResume}
                      >
                        {currentResume}
                      </div>
                      <div className="t-xs">Uploading a new file replaces your current resume.</div>
                    </div>
                    <Badge tone="plain">On file</Badge>
                  </div>
                </div>
              ) : profileLoading ? (
                <Skel h={68} r={18} />
              ) : (
                <div className="ld-empty">
                  <span className="ld-empty-icon">
                    <LuFileText size={18} />
                  </span>
                  <div className="t-sm t-ink" style={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
                    No resume on file
                  </div>
                  <div className="t-xs measure">Upload one to unlock job matches, skills and coaching.</div>
                </div>
              )}

              <div
                className={`ru-drop${dragOver ? " on" : ""}`}
                onClick={() => inputRef.current && inputRef.current.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && inputRef.current) inputRef.current.click();
                }}
              >
                <LuUpload size={22} className="ru-drop-ico" />
                <div className="t-sm t-ink" style={{ fontWeight: 600 }}>
                  Drag &amp; drop your resume here
                </div>
                <div className="t-xs">PDF, DOC or DOCX</div>
                <button
                  type="button"
                  className="btn btn-ghost btn-pill btn-sm mt-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (inputRef.current) inputRef.current.click();
                  }}
                >
                  Choose file
                </button>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>

              {chosenFiles.length > 0 ? (
                <div>
                  <div className="ld-eyebrow mb-1">Your chosen file</div>
                  {chosenFiles.map((f, i) => (
                    <div key={i} className="ru-file">
                      <span className="ld-stat-icon">
                        <LuFileText size={18} />
                      </span>
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div
                          className="t-sm t-ink"
                          style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={f.name}
                        >
                          {f.name}
                        </div>
                        <div className="t-xs">Will replace your current resume on upload.</div>
                      </div>
                      <Badge tone="accent">Ready</Badge>
                    </div>
                  ))}
                </div>
              ) : null}

              <button type="submit" className="btn btn-pill btn-mint btn-lg btn-full" disabled={uploading}>
                {uploading ? (
                  <span
                    className="spinner"
                    style={{
                      borderColor: "color-mix(in oklab, var(--accent-ink) 35%, transparent)",
                      borderTopColor: "var(--accent-ink)",
                    }}
                  />
                ) : (
                  <LuUpload size={16} />
                )}
                Upload and save
              </button>
            </div>
          </section>
        </div>
      </form>

      {/* ---------- upload-in-flight dialog ---------- */}
      <Dialog open={uploading}>
        <div className="ru-stage">
          <span className="ld-step-icon ru-breathe">
            <LuSparkles size={22} />
          </span>
          <div className="t-h3">Reviewing your resume</div>
          <p className="t-sm measure">
            Our AI is reviewing your resume and ensuring it understands every detail...
          </p>
          <div className="row" style={{ gap: 8 }}>
            <Spinner />
            <span className="t-xs">Uploading &amp; replacing your file</span>
          </div>
          <p className="t-xs measure">
            🚀 Meanwhile, check out the video below to see how our magic works and how CareerSavvy can help you take
            the next step in your career!
          </p>
          <Btn variant="ghost" size="sm" className="btn-pill" onClick={watchVideo}>
            <LuExternalLink size={14} /> Watch video
          </Btn>
        </div>
      </Dialog>

      {/* ---------- staged processing / success dialog (timings identical to legacy) ---------- */}
      <Dialog open={stageOpen}>
        {stage !== "ready" ? (
          <div className="ru-stage">
            <span className="ld-step-icon ru-breathe">
              <LuSparkles size={22} />
            </span>
            <div className="t-h3">Building your profile</div>
            <div className="bar" style={{ width: "100%" }}>
              <span style={{ width: `${stageInfo ? stageInfo.pct : 8}%` }} />
            </div>
            <div className="ru-steps">
              {STAGE_ORDER.map((key, i) => {
                const state = stageIdx > i ? "done" : stageIdx === i ? "active" : "";
                return (
                  <div key={key} className={`ru-step${state ? ` ${state}` : ""}`}>
                    <span className="ru-step-dot">
                      {state === "done" ? (
                        <LuCheck size={13} />
                      ) : state === "active" ? (
                        <span className="spinner" style={{ width: 12, height: 12 }} />
                      ) : (
                        <span className="ru-step-idle" />
                      )}
                    </span>
                    <span>{STAGES[key].text}</span>
                  </div>
                );
              })}
            </div>
            <p className="t-xs measure">
              This takes about a minute. Please keep this tab open while we analyse your resume.
            </p>
          </div>
        ) : (
          <div className="ru-stage">
            <span className="ld-step-icon">
              <LuCheck size={22} />
            </span>
            <div className="t-h3">Time to unleash our magic!</div>
            <p className="t-sm measure">
              Almost there! Please log in again to continue your journey with us.
            </p>

            {/* parsed profile summary */}
            <div className="card" style={{ borderRadius: 18, width: "100%", textAlign: "left", padding: 14, boxShadow: "none" }}>
              <div className="row" style={{ gap: 10 }}>
                <span className="avatar">{initialsOf(profileData?.first_name || userEmail)}</span>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="t-sm t-ink" style={{ fontWeight: 600 }}>
                    {[profileData?.first_name, profileData?.last_name].filter(Boolean).join(" ") || "Your profile"}
                  </div>
                  <div className="t-xs row" style={{ gap: 6 }}>
                    <LuMail size={12} /> {userEmail}
                  </div>
                  {profileData?.mobile_no ? (
                    <div className="t-xs row" style={{ gap: 6 }}>
                      <LuPhone size={12} /> {profileData.mobile_no}
                    </div>
                  ) : null}
                </div>
                <Badge tone="accent">
                  <LuCheck size={12} /> Resume parsed
                </Badge>
              </div>
            </div>

            <button type="button" className="btn btn-pill btn-mint btn-full" onClick={remConfirm}>
              <LuLogOut size={15} /> Go to log-out {isCounting ? <span>({countdown})</span> : null}
            </button>
          </div>
        )}
      </Dialog>
    </div>
  );
}
